import type { ReactElement } from 'react';
import { Button, Heading, Image, Page, Paragraph } from '@zillow/constellation-10';
import errorImage from '../images/error.svg';

export type ErrorPageProps = {
  /**
   * Optional custom error message. Defaults to "Something went wrong"
   */
  errorMessage?: string;
  /**
   * Optional additional details about the error
   */
  errorDetails?: string;
  /**
   * Optional Next.js Link or anchor element that will be rendered as a styled Button.
   * You have full control over the element's props (href, onClick, prefetch, etc.)
   * @example <Link href="/">Go Home</Link>
   * @example <a href="/">Go Home</a>
   * @example <Link href="/" onClick={() => trackEvent('home')}>Go Home</Link>
   */
  action?: React.ReactNode;
};

export default function ErrorPage({
  errorDetails,
  errorMessage = 'Something went wrong',
  action,
}: ErrorPageProps): ReactElement {
  // Handle different bundler configurations: some return objects with .src, others return string URLs
  const imageSrc = typeof errorImage === 'string' ? errorImage : errorImage?.src || '';

  return (
    <Page.Root css={{ backgroundColor: 'bg.neutral', height: '100%' }}>
      <Page.Content
        css={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 'default',
          paddingTop: 'looser',
        }}
      >
        <Image
          src={imageSrc}
          alt="picture of a dog holding an electric plug in its mouth"
          css={{
            height: '160px',
            width: '160px',
          }}
        />
        <Heading level={1} css={{ textStyle: 'responsive.heading-md' }}>
          {errorMessage}
        </Heading>
        {errorDetails ? <Paragraph>{errorDetails}</Paragraph> : null}
        {action && (
          <Button tone="brand" emphasis="filled" asChild>
            {action}
          </Button>
        )}
      </Page.Content>
    </Page.Root>
  );
}
